export type SameSite = 'Lax' | 'Strict' | 'None';

export interface CookieOptions {
  path?: string;
  domain?: string;
  /**
   * Max-Age in seconds
   */
  maxAge?: number;
  expires?: Date;
  sameSite?: SameSite;
  secure?: boolean;
}

export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
) {
  if (typeof document === 'undefined') return;

  const encodedValue = encodeURIComponent(value);
  const path = options.path ?? '/';
  const sameSite: SameSite = options.sameSite ?? 'Lax';

  const shouldSecure =
    typeof options.secure === 'boolean'
      ? options.secure
      : sameSite === 'None' ||
        (typeof location !== 'undefined' && location.protocol === 'https:');

  let cookie = `${name}=${encodedValue}`;

  if (path) cookie += `; Path=${path}`;
  if (options.domain) cookie += `; Domain=${options.domain}`;

  if (typeof options.maxAge === 'number') {
    cookie += `; Max-Age=${Math.floor(options.maxAge)}`;
  } else if (options.expires) {
    cookie += `; Expires=${options.expires.toUTCString()}`;
  }

  cookie += `; SameSite=${sameSite}`;
  if (shouldSecure) cookie += '; Secure';

  document.cookie = cookie;
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const all = document.cookie;
  if (!all) return null;

  for (const part of all.split(';')) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;

    const key = trimmed.slice(0, eqIdx);
    if (key !== name) continue;

    const rawValue = trimmed.slice(eqIdx + 1);
    try {
      return decodeURIComponent(rawValue);
    } catch {
      return rawValue;
    }
  }

  return null;
}

export function deleteCookie(
  name: string,
  options: Pick<CookieOptions, 'path' | 'domain'> = {}
) {
  setCookie(name, '', { ...options, maxAge: 0 });
}
